import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { DxTreeListComponent } from "devextreme-angular";
import { TranslateService } from '@ngx-translate/core';
import { ClassificationSystem } from "../Model/classification/ClassificationSystem";
import { ClassificationClass } from "../Model/classification/ClassificationClass";
import { ClassificationFeature } from "../Model/classification/ClassificationFeature";
import { ClassificationService } from "../Services/classification.service";
import { ColumnButtonClickEvent, SelectionChangedEvent } from "devextreme/ui/tree_list";
import { confirm } from "devextreme/ui/dialog";


@Component({
    selector: "classificationTree",
    templateUrl: "classificationTree.component.html",
    styleUrls: ["./classificationTree.component.css"],
    standalone: false
})
export class ClassificationTreeComponent implements OnInit, OnChanges {

  @ViewChild(DxTreeListComponent, { static: true }) treeList: DxTreeListComponent;

  @Input() selectedClassificationSystem: ClassificationSystem;
  @Input() loadingClassificationClasses: boolean;
  @Input() classificationClasses: ClassificationClass[];
  @Output() selectedClassificationClassChanged = new EventEmitter<ClassificationClass>();
  @Output() loadingClassificationClassesChange = new EventEmitter<boolean>();
  @Output() classificationClassesChange = new EventEmitter<ClassificationClass[]>();

  selectedClassificationClass: ClassificationClass;

  constructor(public classificationService: ClassificationService, public translate: TranslateService) {
    this.createClassificationClass = this.createClassificationClass.bind(this);
    this.duplicateClassificationClass = this.duplicateClassificationClass.bind(this);
    this.deleteClassificationClass = this.deleteClassificationClass.bind(this);
  }

  ngOnInit(): void {
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedClassificationSystem) {
      await this.refresh();
    }
  }

  async refresh() {
    if (this.selectedClassificationSystem != undefined) {
      this.loadingClassificationClasses = true;
      this.loadingClassificationClassesChange.emit(this.loadingClassificationClasses);
      this.classificationClasses = await this.classificationService.getClassificationClassesWithoutFeatures(this.selectedClassificationSystem.id);
      this.loadingClassificationClasses = false;
      this.classificationClassesChange.emit(this.classificationClasses);
      this.loadingClassificationClassesChange.emit(this.loadingClassificationClasses);
    } else {
      this.classificationClasses = [];
      this.classificationClassesChange.emit(this.classificationClasses);
    }
  }

  async createClassificationClass(event: ColumnButtonClickEvent) {
    var newClassificationClass = new ClassificationClass();
    newClassificationClass.number = this.findUniqueClassificationClassNumber();
    newClassificationClass.name = this.translate.instant("Neue Klassifizierung");
    newClassificationClass.parentId = event.row.key;
    newClassificationClass.assignable = true;
    newClassificationClass.classificationSystemId = this.selectedClassificationSystem.id;
    newClassificationClass.features = new Array<ClassificationFeature>();
    var createdClassificationClass = await this.classificationService.addClassificationClass(newClassificationClass);
    this.classificationClasses.push(createdClassificationClass);
    this.classificationClassesChange.emit(this.classificationClasses);
    this.treeList.instance.expandRow(event.row.key);
  }

  async duplicateClassificationClass(event: ColumnButtonClickEvent) {
    var newClassificationClass: ClassificationClass = structuredClone(event.row.data);
    await this.classificationService.duplicateClassificationClass(newClassificationClass);
    this.refresh();
  }

  async deleteClassificationClass(event: ColumnButtonClickEvent) {
    var deleteClassificationClass: ClassificationClass = event.row.data;
    let dialogResult = await confirm(this.translate.instant("ConfirmDeleteClassificationClass"), this.translate.instant("Klasse löschen"));
    if (!dialogResult) {
      return;
    }
    this.loadingClassificationClasses = true;
    this.loadingClassificationClassesChange.emit(this.loadingClassificationClasses);
    await this.classificationService.deleteClassificationClass(deleteClassificationClass);
    var deleteClassificationClassIndex = this.classificationClasses.findIndex(x => x.id == deleteClassificationClass.id);
    this.classificationClasses.splice(deleteClassificationClassIndex, 1);
    this.classificationClassesChange.emit(this.classificationClasses);
    this.loadingClassificationClasses = false;
    this.loadingClassificationClassesChange.emit(this.loadingClassificationClasses);
  }

  async createTopClassificationClass() {
    var newClassificationClass = new ClassificationClass();
    newClassificationClass.number = this.findUniqueClassificationClassNumber();
    newClassificationClass.name = this.translate.instant("Neue Klassifizierung");
    newClassificationClass.classificationSystemId = this.selectedClassificationSystem.id;
    newClassificationClass.features = new Array<ClassificationFeature>();
    var createdClassificationClass = await this.classificationService.addClassificationClass(newClassificationClass);
    this.classificationClasses.push(createdClassificationClass);
    this.classificationClassesChange.emit(this.classificationClasses);
  }

  onClassificationClassSelectionChanged(e: SelectionChangedEvent) {
    let selected = e.currentSelectedRowKeys;
    if (selected) {
      this.selectedClassificationClass = this.classificationClasses.find(x => x.id == selected[0]);
    } else {
      this.selectedClassificationClass = undefined;
    }
    this.selectedClassificationClassChanged.emit(this.selectedClassificationClass);
  }

  get noDataText() {
    if (this.selectedClassificationSystem == undefined) {
      return this.translate.instant("Klassifizierungs-System auswählen");
    }
    else {
      return this.translate.instant("Keine Klassifizierungen vorhanden");
    }
  }

  findUniqueClassificationClassNumber(): string {
    const numbers = new Set(this.classificationClasses.map((classificationClass) => Number(classificationClass.number))
      .filter((n) => !Number.isNaN(n)));

    let number = 1;
    while (numbers.has(number)) {
      number++;
    }

    return number.toString();
  }
}
